import { Typography } from 'components/base';
import { Tooltip } from 'components/atoms';
import { memo, useCallback, useEffect, useRef, useState } from 'react';

interface TruncatedLabelProps {
  text: string;
  noWrap?: boolean;
}

export const TruncatedLabel = memo<TruncatedLabelProps>(
  ({ text, noWrap = true }) => {
    const labelRef = useRef<HTMLDivElement | null>(null);
    const [isTruncated, setIsTruncated] = useState(false);

    const checkTruncation = useCallback(() => {
      if (!labelRef.current) return;

      const { scrollWidth, clientWidth } = labelRef.current;
      setIsTruncated(scrollWidth > clientWidth);
    }, []);

    useEffect(() => {
      // Initial truncation check
      checkTruncation();

      if (!labelRef.current) return undefined;

      // Use ResizeObserver if available, fallback to window resize
      if (typeof ResizeObserver !== 'undefined') {
        const observer = new ResizeObserver(checkTruncation);
        observer.observe(labelRef.current);
        return () => observer.disconnect();
      }
      // Fallback for older browsers
      window.addEventListener('resize', checkTruncation);
      return () => window.removeEventListener('resize', checkTruncation);
    }, [text, checkTruncation]);

    return (
      <Tooltip
        title={isTruncated ? text : ''}
        disableInteractive
        enterNextDelay={500}
      >
        <Typography ref={labelRef} variant="body2" py={0.4} noWrap={noWrap}>
          {text}
        </Typography>
      </Tooltip>
    );
  },
);

TruncatedLabel.displayName = 'TruncatedLabel';
